import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAnonId,
  getHandle,
  hasHandle,
  HandleModal,
  haptic,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
} from "@karrtopelka/game-kit";
import type { CardProps } from "@karrtopelka/cards/types";

import { buildDrawCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { useDrawStore, type DrawState } from "./store.js";

/** Reveal theater length (cycling highlight before settling on the outcome). */
const SPIN_MS = 2600;

/** One leaderboard fetch shared by the desktop rail and the mobile summary board. */
type BoardApi = ReturnType<typeof useLeaderboard>;

const SIBLING_GAMES = [
  { name: "rocket", hook: "one shared flight", href: "https://rocket.karrtopelka.com" },
  { name: "plinko", hook: "one ball, 470x edges", href: "https://plinko.karrtopelka.com" },
  { name: "aim", hook: "raw reflex check", href: "https://aim.karrtopelka.com" },
] as const;

export function App() {
  useEffect(() => trackPageView("draw"), []);

  const board = useLeaderboard("draw");
  const [handle, setHandle] = useState<string | null>(() => getHandle());

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-5 px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:items-start lg:justify-center lg:gap-x-10 lg:gap-y-8 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      {/* Signature ambient: one big soft pink orb drifting over the starfield
          (starfield itself is body::after in index.css). Behind everything. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -z-10 h-[34rem] w-[34rem] animate-float rounded-full right-[-11rem] top-[16%]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,111,165,0.09) 0%, rgba(255,111,165,0.03) 45%, transparent 70%)",
        }}
      />

      <header className="flex animate-fade-up flex-col items-center lg:col-span-3">
        <h1 className="text-3xl font-extrabold tracking-tight lg:text-5xl">
          draw
          <span className="text-accent [text-shadow:0_0_22px_rgba(255,111,165,0.6)]">
            .
          </span>
        </h1>
        <p className="text-sm text-text-muted lg:mt-1 lg:text-base">
          pick a number. 19:00 UTC. everyone watches the same reveal.
        </p>
      </header>

      <LeftRail />

      <Game board={board} handle={handle} onHandleChange={setHandle} />

      <RightRail board={board} handle={handle} />

      <footer
        className="mt-2 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-2 lg:gap-3"
        style={{ animationDelay: "320ms" }}
      >
        {/* mobile: tiny text links, unchanged */}
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://rocket.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try rocket — one shared flight every 10 min →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>

        {/* desktop: more-games strip — other cabinets on the floor */}
        <div className="hidden lg:flex lg:items-stretch lg:gap-3">
          {SIBLING_GAMES.map((g) => (
            <a
              key={g.name}
              href={g.href}
              className="group flex w-44 flex-col gap-0.5 rounded-lg border border-border bg-surface/60 px-4 py-3 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
            >
              <span className="font-display text-sm font-bold text-text">
                {g.name}
                <span className="text-accent">.</span>
              </span>
              <span className="text-xs text-text-faint group-hover:text-text-muted">
                {g.hook}
              </span>
            </a>
          ))}
        </div>
        <a
          href="https://karrtopelka.com"
          className="hidden transition-colors duration-200 hover:text-accent lg:block"
        >
          karrtopelka.com — the whole floor →
        </a>
      </footer>
    </main>
  );
}

/** Desktop-only left rail: how it works + tonight's numbers from the store. */
function LeftRail() {
  const state = useDrawStore((s) => s.state);
  return (
    <aside
      className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-4"
      style={{ animationDelay: "160ms" }}
    >
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          how it works
        </h2>
        <ul className="mt-2 flex flex-col gap-1.5 text-sm text-text-faint">
          <li>pick one of twelve.</li>
          <li>19:00 utc — everyone watches the same reveal.</li>
          <li>call it nights in a row. that's the number.</li>
        </ul>
      </div>
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          tonight
        </h2>
        <dl className="mt-2 flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-text-faint">your pick</dt>
            <dd className="font-display text-lg font-bold text-accent">
              {state && state.myPick !== null ? state.myPick + 1 : "—"}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-text-faint">your streak</dt>
            <dd className="font-display text-lg font-bold text-gold">
              {state ? state.myStreak : 0} 🔮
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-text-faint">in tonight</dt>
            <dd className="font-display text-lg font-bold text-text">
              {state ? state.count : "—"}
            </dd>
          </div>
        </dl>
      </div>
    </aside>
  );
}

/** Desktop-only right rail: the leaderboard stays on the table during play. */
function RightRail({ board, handle }: { board: BoardApi; handle: string | null }) {
  return (
    <aside
      className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-3"
      style={{ animationDelay: "240ms" }}
    >
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-muted">
          longest call streaks
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={handle}
          formatValue={(v) => `${v} 🔮`}
          emptyLabel="nobody's called one yet — 19:00 UTC daily."
          range={board.range}
          onRange={board.setRange}
        />
      </div>
    </aside>
  );
}

function useCountdown(target: number | null, offset: number): number {
  const [left, setLeft] = useState(() =>
    target === null ? 0 : Math.max(0, target - (Date.now() + offset)),
  );
  useEffect(() => {
    if (target === null) return;
    const t = setInterval(
      () => setLeft(Math.max(0, target - (Date.now() + offset))),
      250,
    );
    return () => clearInterval(t);
  }, [target, offset]);
  return left;
}

function fmtCountdown(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function Game({
  board,
  handle,
  onHandleChange,
}: {
  board: BoardApi;
  handle: string | null;
  onHandleChange: (h: string) => void;
}) {
  const state = useDrawStore((s) => s.state);
  const offset = useDrawStore((s) => s.clockOffset);
  const reveal = useDrawStore((s) => s.reveal);
  const apply = useDrawStore((s) => s.apply);
  const setReveal = useDrawStore((s) => s.setReveal);

  const [showHandle, setShowHandle] = useState(false);
  const [pendingChoice, setPendingChoice] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);
  const [spinFlash, setSpinFlash] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/draw/state?anonId=${getAnonId()}`);
      if (!res.ok) return;
      apply((await res.json()) as DrawState);
    } catch {
      /* poll again later */
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const left = useCountdown(state?.endsAt ?? null, offset);

  // Poll cadence: lazy normally, eager in the last minute, reveal at zero.
  useEffect(() => {
    if (!state) return;
    const t = setInterval(() => void refresh(), left < 90_000 ? 5_000 : 30_000);
    return () => clearInterval(t);
  }, [state, left < 90_000, refresh]);

  // The shared moment: countdown hits zero while the page is open → spin the
  // highlight, then refetch (which lazily resolves server-side) and settle.
  const revealed = useRef(false);
  useEffect(() => {
    if (!state || left > 0 || revealed.current) return;
    revealed.current = true;
    setReveal("spinning");
    haptic.tap();
    const spinT = setInterval(() => {
      setSpinFlash(Math.floor(Math.random() * 12));
      haptic.tap();
    }, 130);
    setTimeout(() => {
      clearInterval(spinT);
      setSpinFlash(null);
      void refresh().then(() => {
        setReveal("shown");
        revealed.current = false;
      });
    }, SPIN_MS);
  }, [state, left, refresh, setReveal]);

  // Win celebration once the resolved round lands (whether live or on a later visit).
  const celebrated = useRef<number | null>(null);
  useEffect(() => {
    const last = state?.last;
    if (!last?.me?.hit || celebrated.current === last.roundId) return;
    celebrated.current = last.roundId;
    haptic.newBest();
    setCard(buildDrawCard(Math.max(state?.myStreak ?? 1, 1), getHandle() ?? "you"));
    void board.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.last?.roundId, state?.last?.me?.hit]);

  const doPick = async (choice: number) => {
    setError(null);
    try {
      const res = await fetch("/api/draw/pick", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ anonId: getAnonId(), handle: getHandle(), choice }),
      });
      if (!res.ok) {
        const { error: e } = (await res.json().catch(() => ({ error: "" }))) as {
          error?: string;
        };
        setError(e || "pick failed");
        return;
      }
      haptic.land();
      track("draw", "play_start");
      void refresh();
    } catch {
      setError("network hiccup — try again");
    }
  };

  const onPick = (choice: number) => {
    if (!state || state.locked || state.myPick !== null) return;
    if (hasHandle()) void doPick(choice);
    else {
      setPendingChoice(choice);
      setShowHandle(true);
    }
  };

  if (!state) {
    return (
      <p className="animate-fade-up text-sm text-text-faint">loading today's draw…</p>
    );
  }

  const spinning = reveal === "spinning";
  const last = state.last;
  const maxPick = Math.max(1, ...state.distribution);

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-4 lg:max-w-2xl lg:justify-self-center">
      {/* the clock — the whole point. desktop: a marquee sign over the table */}
      <div
        className={`flex animate-fade-up flex-col items-center lg:w-full lg:rounded-lg lg:border lg:bg-surface/40 lg:px-8 lg:py-6 lg:backdrop-blur lg:transition-all lg:duration-200 ${
          spinning
            ? "lg:border-accent/50 lg:shadow-glow"
            : left < 90_000 && left > 0
              ? "lg:border-danger/40 lg:shadow-glow-danger"
              : "lg:border-border"
        }`}
        style={{ animationDelay: "80ms" }}
      >
        <span
          className={`font-display text-6xl font-extrabold tabular-nums lg:text-8xl ${
            spinning
              ? "animate-breathe text-accent"
              : left < 90_000 && left > 0
                ? "text-danger"
                : "text-text"
          }`}
        >
          {spinning ? "··:··:··" : fmtCountdown(left)}
        </span>
        <span className="text-xs uppercase tracking-widest text-text-faint lg:mt-1 lg:text-sm">
          {spinning
            ? "drawing…"
            : state.locked
              ? "picks locked — reveal imminent"
              : `until draw #${state.roundId} · ${state.count} in`}
        </span>
      </div>

      {/* the twelve — crowd heat glows pink under popular numbers */}
      <div
        className="grid w-full animate-fade-up grid-cols-4 gap-2 lg:grid-cols-6 lg:gap-3"
        style={{ animationDelay: "160ms" }}
      >
        {Array.from({ length: state.slots }, (_, i) => {
          const mine = state.myPick === i;
          const flashed = spinFlash === i;
          const lastOutcome = reveal === "shown" && last && last.outcome === i;
          const pickable = !state.locked && state.myPick === null && !spinning;
          const heat = state.distribution[i] ?? 0;
          const heatFrac = heat / maxPick;
          const plain = !flashed && !lastOutcome && !mine;
          return (
            <button
              key={i}
              type="button"
              disabled={!pickable}
              onClick={() => onPick(i)}
              title={heat > 0 ? `${heat} on ${i + 1}` : undefined}
              className={`relative flex aspect-square flex-col items-center justify-center rounded-md border text-2xl font-bold transition-all duration-200 active:scale-95 lg:text-3xl ${
                flashed
                  ? "border-accent bg-accent text-bg"
                  : lastOutcome
                    ? "border-gold bg-gold/15 text-gold shadow-glow-gold"
                    : mine
                      ? "animate-neon-pulse border-accent bg-accent/15 text-accent shadow-glow"
                      : pickable
                        ? "border-border bg-surface-raised text-text hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
                        : "border-border bg-surface text-text-muted"
              }`}
              style={
                plain && heat > 0
                  ? {
                      backgroundImage: `radial-gradient(90% 65% at 50% 105%, rgba(255,111,165,${(
                        0.08 +
                        0.24 * heatFrac
                      ).toFixed(3)}), transparent)`,
                    }
                  : undefined
              }
            >
              <span className="font-display">{i + 1}</span>
              {heat > 0 && (
                <span
                  className={`absolute bottom-1 right-1.5 font-mono text-[9px] lg:bottom-1.5 lg:right-2 lg:text-[11px] ${
                    heat === maxPick ? "text-accent/80" : "text-text-faint"
                  }`}
                >
                  {heat}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {state.myPick !== null && !spinning && (
        <p className="animate-fade-up text-sm text-text-muted lg:text-base">
          you're on <span className="font-display text-accent">{state.myPick + 1}</span>{" "}
          — be here at the reveal
        </p>
      )}
      {state.myStreak > 0 && (
        <p className="animate-fade-up rounded-full border border-gold/50 bg-gold/10 px-4 py-1.5 text-sm font-semibold text-gold lg:text-base">
          🔮 {state.myStreak} draw{state.myStreak > 1 ? "s" : ""} called in a row
        </p>
      )}
      {error && <p className="text-sm text-accent">{error}</p>}

      {/* yesterday's verdict */}
      {last && reveal !== "spinning" && (
        <div
          className={`flex w-full animate-fade-up flex-col items-center gap-1 rounded-lg border bg-surface/40 p-3 text-sm lg:gap-1.5 lg:p-5 lg:text-base ${
            last.me?.hit
              ? "animate-flash-win border-win/40"
              : last.me
                ? "border-danger/30"
                : "border-border"
          }`}
          style={{ animationDelay: "240ms" }}
        >
          <span className="text-text-muted">
            draw #{last.roundId}: the number was{" "}
            <span className="font-display text-lg text-gold lg:text-3xl">
              {last.outcome + 1}
            </span>
            {" · "}
            {last.winners} of {last.total} called it
          </span>
          {last.me && (
            <span className={last.me.hit ? "font-semibold text-win" : "text-text-faint"}>
              {last.me.hit
                ? "you called it 🔮"
                : `you were on ${last.me.picked + 1} — tomorrow's yours`}
            </span>
          )}
        </div>
      )}

      {card && (
        <div className="flex w-full animate-fade-up justify-center">
          <ShareSheet
            card={card}
            replyLink="draw.karrtopelka.com/r"
            caption={`called the daily draw. streak: ${card.display}. ${card.provocation}`}
            loadAssets={loadCardAssets}
          />
        </div>
      )}

      {/* mobile keeps the leaderboard below the game; desktop moves it to the rail */}
      <section
        className="flex w-full animate-fade-up flex-col items-center gap-3 border-t border-border pt-6 lg:hidden"
        style={{ animationDelay: "320ms" }}
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          longest call streaks
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={handle}
          formatValue={(v) => `${v} 🔮`}
          emptyLabel="nobody's called one yet — 19:00 UTC daily."
          range={board.range}
          onRange={board.setRange}
        />
      </section>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandleChange(h);
            if (pendingChoice !== null) void doPick(pendingChoice);
          }}
        />
      )}
    </div>
  );
}
